import { useQuery } from '@tanstack/react-query'
import * as api from '../services/executionsApi'
import type { ExecutionOutcome, ExecutionQuery } from '../types/executions'
import { FREQUENT, LIVE } from '../utils/polling'

/**
 * Execution History queries.
 *
 * The filters are part of the list's key by value, and the fetch is called
 * through a closure, for the reason `useConnections` gives: the key and the
 * argument must move together, or a filter change would serve the previous
 * filter's cached page.
 */

/**
 * One filtered page of runs. On FREQUENT: the newest rows are exactly the ones
 * still moving, and a run going stale is a change nobody else announces.
 */
export function useExecutions(query: ExecutionQuery) {
  return useQuery({
    queryKey: ['observatory', 'executions', query],
    queryFn: () => api.getExecutions(query),
    refetchInterval: FREQUENT,
    // Keeps the previous page on screen while the next loads, so paging does not
    // blank the table.
    placeholderData: (previous) => previous,
  })
}

/**
 * How often a run's detail re-asks, by its outcome: LIVE while it is running
 * (counters and heartbeats move every batch), FREQUENT while stale (a stale run
 * can be resumed, and nothing else would tell this page), never once terminal —
 * a finished run does not change.
 */
export function executionRefetchInterval(outcome: ExecutionOutcome | undefined): number | false {
  if (outcome === 'running') return LIVE
  if (outcome === 'stale') return FREQUENT
  return false
}

/** One run in full. Disabled with no id. */
export function useExecution(runId: string) {
  return useQuery({
    queryKey: ['observatory', 'execution', runId],
    queryFn: () => api.getExecution(runId),
    enabled: Boolean(runId),
    refetchInterval: (query) => executionRefetchInterval(query.state.data?.outcome),
  })
}
